/**
 * Phase 9B draft queue → 16C outbox (plan C9, decision P3).
 *
 * `pelora-drafts` kept chart text unencrypted on any device. 16C stops writing it everywhere:
 * - enrollment: this user's drafts become CHART_CREATE ops in the encrypted outbox (new entry id,
 *   observedAt = the draft time), then the database is deleted;
 * - any other signed-in session (laptop, phone without offline mode): this user's drafts are sent
 *   online once, then the database is deleted when nothing is left in it.
 */
import { openLegacyDrafts, LegacyDraftRecord, LegacyDraftStore } from './platform/kvBackend';
import type { LocalActions } from './localActions';

export type LegacyDraftOpener = () => Promise<LegacyDraftStore | null>;

const MAX_AGE_MS = 72 * 3600 * 1000;

interface DraftPayload { entryType?: unknown; data?: unknown }

const asPayload = (d: LegacyDraftRecord): DraftPayload => (d.payload && typeof d.payload === 'object' ? d.payload as DraftPayload : {});

/** @returns the number of drafts moved into the outbox. */
export async function migrateLegacyDrafts(userId: string, local: LocalActions, open: LegacyDraftOpener = openLegacyDrafts, nowMs = Date.now()): Promise<number> {
  const store = await open();
  if (!store) return 0;
  let moved = 0;
  for (const draft of await store.list()) {
    if (draft.userId !== userId || draft.failedReason || nowMs - Date.parse(draft.createdAt) > MAX_AGE_MS) continue;
    const p = asPayload(draft);
    if (typeof p.entryType !== 'string') continue;
    await local.createChartEntry(draft.patientId, draft.visitId, { entryType: p.entryType, data: p.data, observedAt: draft.createdAt });
    await store.remove(draft.id);
    moved += 1;
  }
  await store.destroy();
  return moved;
}

/** Online sessions without offline mode: send this user's drafts, then drop the database if empty. */
export async function flushLegacyDraftsOnline(
  userId: string,
  send: (patientId: string, visitId: string, payload: { entryType: string; data: unknown }) => Promise<unknown>,
  open: LegacyDraftOpener = openLegacyDrafts,
): Promise<number> {
  const store = await open();
  if (!store) return 0;
  let sent = 0;
  for (const draft of await store.list()) {
    if (draft.userId !== userId) continue;
    const p = asPayload(draft);
    try {
      if (!draft.failedReason && typeof p.entryType === 'string') {
        await send(draft.patientId, draft.visitId, { entryType: p.entryType, data: p.data });
        sent += 1;
      }
      await store.remove(draft.id);
    } catch {
      // still unreachable or refused: leave it; it expires after 72 h
    }
  }
  if ((await store.list()).length === 0) await store.destroy();
  return sent;
}
